import React from 'react';
import { Helmet } from 'react-helmet-async';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { StaticPage, TopbarContainer } from '../../containers';
import {
  LayoutSingleColumn,
  LayoutWrapperTopbar,
  LayoutWrapperMain,
  LayoutWrapperFooter,
  Button,
  Footer,
} from '../../components';
import { twitterPageURL } from '../../util/urlHelpers';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import config from '../../config';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { searchListings } from '../SearchPage/SearchPage.duck';
import SectionCityListings from '../../components/SectionCityListings/SectionCityListings';
import PropTypes from 'prop-types';

import css from './CityLandingPage.module.css';

export const CityLandingPageComponent = props => {
  const {
    listings,
    history,
    intl,
    rootClassName,
    className,
    cityKey,
    cityConfig,
    cityLabel,
  } = props;

  const classes = classNames(rootClassName || css.root, className);
  const title = intl.formatMessage({ id: `LandingPage${cityKey}.seoTitle` });
  const description = intl.formatMessage({ id: `LandingPage${cityKey}.seoDescription` });
  const schemaTitle = intl.formatMessage({ id: `LandingPage${cityKey}.schemaTitle` });
  const schemaDescription = intl.formatMessage({ id: `LandingPage${cityKey}.schemaDescription` });

  const handleExploreClick = () => {
    history.push(`/s?address=${cityConfig.searchParams.address}&bounds=${cityConfig.searchParams.bounds}`);
  };

  return (
    <StaticPage
      className={classes}
      title={title}
      description={description}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        name: schemaTitle,
        description: schemaDescription,
      }}
    >
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="twitter:title" content={title} />
        <meta property="twitter:description" content={description} />
      </Helmet>

      <LayoutSingleColumn>
        <LayoutWrapperTopbar>
          <TopbarContainer />
        </LayoutWrapperTopbar>

        <LayoutWrapperMain>
          {/* Hero section */}
          <div className={css.hero} style={{ backgroundImage: `url(${cityConfig.image})` }}>
            <div className={css.heroOverlay}></div>
            <div className={css.heroContent}>
              <h1 className={css.title}>
                <span className="tree-line-1">
                  <FormattedMessage id={`LandingPage${cityKey}.titleLine1`} />
                </span>
                <span className="tree-line-2">
                  <FormattedMessage id={`LandingPage${cityKey}.titleLine2`} />
                </span>

              </h1>
              <p className={css.description} style={{ fontSize: '1rem' }}>
                <FormattedMessage id={`LandingPage${cityKey}.description`} />
              </p>
            </div>
          </div>

          {/* Listings section */}
          <div className={css.sectionListings}>
            <SectionCityListings
              listings={listings}
              city={cityKey}
              cityLabel={cityLabel}
              msgId="SectionCityListings.seeAll"
              intl={intl}
              cityConfig={cityConfig}
            />
            <div className={css.ctaButtonRow}>
              <Button className={css.ctaButtonSmall} onClick={handleExploreClick}>
                <FormattedMessage id={`LandingPage${cityKey}.cta.button`} />
              </Button>
            </div>
          </div>

          {/* Call to Action & SEO Text section */}
          <div className={css.seoSection}>
            <div className={css.seoTextBlockCondensed}>
              <span className={css.seoIntroCondensed}>
                <FormattedMessage id={`LandingPage${cityKey}.zones.title`} />
                {' '}
              </span>
              <span className={css.zonesListCondensed}>
                <FormattedMessage id={`LandingPage${cityKey}.zones.list.0`} />
                {' '}
                <FormattedMessage id={`LandingPage${cityKey}.zones.list.1`} />
                {' '}
                <FormattedMessage id={`LandingPage${cityKey}.zones.list.2`} />
                {' '}
                <FormattedMessage id={`LandingPage${cityKey}.zones.list.3`} />
                {' '}
                {intl.formatMessage({ id: `LandingPage${cityKey}.zones.list.4` }) !== `LandingPage${cityKey}.zones.list.4` && (
                  <FormattedMessage id={`LandingPage${cityKey}.zones.list.4`} />
                )}
              </span>
              <span className={css.seoAfterCondensed}>
                <FormattedMessage id={`LandingPage${cityKey}.zones.after`} />
              </span>
            </div>
          </div>
        </LayoutWrapperMain>

        <LayoutWrapperFooter>
          <Footer
            className={css.footer}
            rootClassName={css.footer}
            twitterPageURL={twitterPageURL}
          />
        </LayoutWrapperFooter>
      </LayoutSingleColumn>
    </StaticPage>
  );
};

CityLandingPageComponent.propTypes = {
  cityKey: PropTypes.string.isRequired,
  cityConfig: PropTypes.shape({
    path: PropTypes.string.isRequired,
    routeName: PropTypes.string.isRequired,
    bounds: PropTypes.string.isRequired,
    origin: PropTypes.string.isRequired,
    address: PropTypes.string.isRequired,
    image: PropTypes.string.isRequired,
    searchParams: PropTypes.object.isRequired,
  }).isRequired,
  listings: PropTypes.array,
  history: PropTypes.object.isRequired,
  intl: intlShape.isRequired,
  rootClassName: PropTypes.string,
  className: PropTypes.string,
};

CityLandingPageComponent.defaultProps = {
  rootClassName: null,
  className: null,
  listings: [],
};

const mapStateToProps = state => {
  const { currentPageResultIds } = state.SearchPage;
  const listings = currentPageResultIds ? getListingsById(state, currentPageResultIds) : [];
  return { listings };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  loadCityListings: () =>
    dispatch(
      searchListings({
        // address: ownProps.cityConfig.address,
        origin: ownProps.cityConfig.origin,
        bounds: ownProps.cityConfig.bounds,
        perPage: 100,
        include: ['author', 'images'],
        'fields.listing': ['title', 'geolocation', 'price'],
        'fields.user': ['profile.displayName', 'profile.abbreviatedName'],
        'fields.image': ['variants.landscape-crop', 'variants.landscape-crop2x'],
        'limit.images': 1,
      })
    ),
});

class CityLandingPageWrapper extends React.Component {
  componentDidMount() {
    this.props.loadCityListings();
  }
  render() {
    return <CityLandingPageComponent {...this.props} />;
  }
}

const CityLandingPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(CityLandingPageWrapper);

export default CityLandingPage;